import { InboxOutlined } from '@ant-design/icons'
import { Alert, Upload } from 'antd'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import { formatFileSize } from '../../lib/stockImportsFormat'
import cn from '../../StockImportsTab/StockImportsTab.module.scss'
import {
  PREVIEW_COLUMNS_LIMIT,
  PREVIEW_ROWS_LIMIT,
} from '../excel/parseExcelWorkbook'
import { parseExcelWorkbookInWorker } from '../excel/parseExcelWorkbookInWorker'
import { resolveExcelDownloadUrl } from '../excel/resolveExcelDownloadUrl'
import UrlExcelTable from '../UrlExcelTable/UrlExcelTable'
import ExcelSheetControls from './ExcelSheetControls/ExcelSheetControls'
import {
  ColumnRole,
  type ColumnRoleOption,
  columnRoles,
  type ExcelMapperValues,
  ParsedSheet,
} from './UrlExcelMapper.types'
import {
  clearUrlExcelMapperCache,
  excelMapperCache,
  setExcelMapperCache,
} from './urlExcelMapperCache'

interface UrlExcelMapperProps {
  values: ExcelMapperValues
  url?: string
  disabled?: boolean
  localFile?: boolean
  onMappingChange: (values: ExcelMapperValues) => void
  renderFields?: (isFileLoaded: boolean) => ReactNode
  roles?: readonly ColumnRoleOption[]
}

const CLIENT_PARSE_SIZE_LIMIT = 40 * 1024 * 1024

const toColumnNumber = (columnIndex: number) => columnIndex + 1
const toColumnIndex = (columnNumber: number) => columnNumber - 1

function UrlExcelMapper({
  values,
  url,
  disabled,
  localFile = false,
  onMappingChange,
  renderFields,
  roles = columnRoles,
}: UrlExcelMapperProps) {
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const cacheKey = useMemo(
    () => (localFile ? '' : (url?.trim() ?? '')),
    [localFile, url]
  )
  const cachedState = cacheKey ? excelMapperCache.get(cacheKey) : undefined
  const [loading, setLoading] = useState(false)
  const [sheets, setSheets] = useState<ParsedSheet[]>(
    () => cachedState?.sheets ?? []
  )
  const [activeSheetName, setActiveSheetName] = useState<string | undefined>(
    () => cachedState?.activeSheetName
  )
  const [selectedSheetNames, setSelectedSheetNames] = useState<string[]>(
    () => cachedState?.selectedSheetNames ?? []
  )
  const [rowsTruncated, setRowsTruncated] = useState(
    () => cachedState?.rowsTruncated ?? false
  )
  const [columnsTruncated, setColumnsTruncated] = useState(
    () => cachedState?.columnsTruncated ?? false
  )
  const [oversizedBytes, setOversizedBytes] = useState<number | null>(null)
  const [localFileName, setLocalFileName] = useState<string>()
  const [blockedDownloadUrl, setBlockedDownloadUrl] = useState<string | null>(
    null
  )
  const showLocalDropzone = localFile || blockedDownloadUrl !== null
  const downloadedBufferRef = useRef<{
    url: string
    buffer: ArrayBuffer
  } | null>(null)
  const localFileRef = useRef<File | null>(null)
  const loadRequestVersionRef = useRef(0)
  const {
    startRowIndex: watchedStartRowIndex,
    endRowIndex: watchedEndRowIndex,
    articleColumnNumber: watchedArticleColumnNumber,
    articleInchColumnNumber: watchedArticleInchColumnNumber,
    barcodeColumnNumber: watchedBarcodeColumnNumber,
    descriptionColumnNumber: watchedDescriptionColumnNumber,
    brandColumnNumber: watchedBrandColumnNumber,
    quantityColumnNumber: watchedQuantityColumnNumber,
    purchaseColumnNumber: watchedPurchaseColumnNumber,
    rrcColumnNumber: watchedRrcColumnNumber,
    currencyColumnNumber: watchedCurrencyColumnNumber,
  } = values

  const activeSheet = useMemo(
    () => sheets.find((sheet) => sheet.name === activeSheetName) ?? sheets[0],
    [activeSheetName, sheets]
  )

  const roleByColumn = useMemo(() => {
    const mapping: Record<number, ColumnRole> = {}
    const values: Record<ColumnRole, unknown> = {
      articleColumnNumber: watchedArticleColumnNumber,
      articleInchColumnNumber: watchedArticleInchColumnNumber,
      barcodeColumnNumber: watchedBarcodeColumnNumber,
      descriptionColumnNumber: watchedDescriptionColumnNumber,
      brandColumnNumber: watchedBrandColumnNumber,
      quantityColumnNumber: watchedQuantityColumnNumber,
      purchaseColumnNumber: watchedPurchaseColumnNumber,
      rrcColumnNumber: watchedRrcColumnNumber,
      currencyColumnNumber: watchedCurrencyColumnNumber,
    }

    roles.forEach((role) => {
      const columnNumber = values[role.value]
      if (typeof columnNumber === 'number')
        mapping[toColumnIndex(columnNumber)] = role.value
    })

    return mapping
  }, [
    roles,
    watchedArticleColumnNumber,
    watchedArticleInchColumnNumber,
    watchedBarcodeColumnNumber,
    watchedBrandColumnNumber,
    watchedDescriptionColumnNumber,
    watchedQuantityColumnNumber,
    watchedPurchaseColumnNumber,
    watchedRrcColumnNumber,
    watchedCurrencyColumnNumber,
  ])

  useEffect(() => {
    loadRequestVersionRef.current += 1
    setLoading(false)
    setOversizedBytes(null)
    setBlockedDownloadUrl(null)
    downloadedBufferRef.current = null
    if (!localFile) {
      localFileRef.current = null
      setLocalFileName(undefined)
    }

    if (!cacheKey) {
      setSheets([])
      setActiveSheetName(undefined)
      setSelectedSheetNames([])
      setRowsTruncated(false)
      setColumnsTruncated(false)
      return
    }

    const nextCachedState = excelMapperCache.get(cacheKey)

    if (nextCachedState) {
      setSheets(nextCachedState.sheets)
      setActiveSheetName(nextCachedState.activeSheetName)
      setSelectedSheetNames(nextCachedState.selectedSheetNames)
      setRowsTruncated(nextCachedState.rowsTruncated)
      setColumnsTruncated(nextCachedState.columnsTruncated)
      return
    }

    setSheets([])
    setActiveSheetName(undefined)
    setSelectedSheetNames([])
    setRowsTruncated(false)
    setColumnsTruncated(false)
  }, [cacheKey, localFile])

  useEffect(() => {
    if (!cacheKey || sheets.length === 0) return

    setExcelMapperCache(cacheKey, {
      sheets,
      activeSheetName,
      selectedSheetNames,
      rowsTruncated,
      columnsTruncated,
    })
  }, [
    activeSheetName,
    cacheKey,
    columnsTruncated,
    rowsTruncated,
    selectedSheetNames,
    sheets,
  ])

  const updateMapping = useCallback(
    (nextValues: ExcelMapperValues) => {
      const currentValues = new Map(Object.entries(values))
      const changedValues: ExcelMapperValues = Object.fromEntries(
        Object.entries(nextValues).filter(
          ([field, value]) => currentValues.get(field) !== value
        )
      )
      if (Object.keys(changedValues).length === 0) return

      onMappingChange(changedValues)
    },
    [values, onMappingChange]
  )

  const sheetNames = sheets.map((sheet) => sheet.name)
  const isFileLoaded = sheets.length > 0

  const applySelectedSheets = useCallback(
    (nextSheetNames: string[]) => {
      setSelectedSheetNames(nextSheetNames)

      if (
        nextSheetNames.length === 0 ||
        nextSheetNames.length === sheets.length
      ) {
        updateMapping({
          sheetSelectionType: 'ALL',
          sheetNames: undefined,
          sheetIndexes: undefined,
        })
        return
      }

      updateMapping({
        sheetSelectionType: 'BY_NAME',
        sheetNames: nextSheetNames.join(', '),
        sheetIndexes: undefined,
      })
    },
    [sheets.length, updateMapping]
  )

  const applyParsedWorkbook = useCallback(
    async (
      arrayBuffer: ArrayBuffer,
      isCurrentRequest: () => boolean,
      toastId: string | number
    ) => {
      const {
        sheets: parsedSheets,
        rowsTruncated: nextRowsTruncated,
        columnsTruncated: nextColumnsTruncated,
      } = await parseExcelWorkbookInWorker(arrayBuffer)
      if (!isCurrentRequest()) {
        toast.dismiss(toastId)
        return
      }
      if (parsedSheets.length === 0) {
        throw new Error('В файле нет листов')
      }

      setSheets(parsedSheets)
      setRowsTruncated(nextRowsTruncated)
      setColumnsTruncated(nextColumnsTruncated)
      setActiveSheetName(parsedSheets[0].name)
      setSelectedSheetNames(parsedSheets.map((sheet) => sheet.name))
      updateMapping({
        sheetSelectionType: 'ALL',
        sheetNames: undefined,
        sheetIndexes: undefined,
      })
      toast.success('Файл загружен. Выберите строку и колонки в таблице', {
        id: toastId,
      })
    },
    [updateMapping]
  )

  const handleLoad = useCallback(
    async (options?: { ignoreSizeLimit?: boolean }) => {
      const requestUrl = url?.trim() ?? ''
      if (!requestUrl) {
        toast.error('Сначала укажите публичную ссылку')
        return
      }

      const requestVersion = ++loadRequestVersionRef.current
      const isCurrentRequest = () =>
        requestVersion === loadRequestVersionRef.current
      setLoading(true)
      setOversizedBytes(null)
      setBlockedDownloadUrl(null)
      const toastId = toast.loading('Загружаю Excel-файл...')
      try {
        const reusableBuffer =
          options?.ignoreSizeLimit &&
          downloadedBufferRef.current?.url === requestUrl
            ? downloadedBufferRef.current.buffer
            : null

        let arrayBuffer: ArrayBuffer

        if (reusableBuffer) {
          arrayBuffer = reusableBuffer
        } else {
          const downloadUrl = await resolveExcelDownloadUrl(requestUrl)
          let response: Response
          try {
            response = await fetch(downloadUrl)
          } catch {
            toast.dismiss(toastId)
            if (isCurrentRequest()) setBlockedDownloadUrl(downloadUrl)
            return
          }
          if (!response.ok) {
            throw new Error('Не удалось скачать Excel-файл')
          }

          const contentType = response.headers.get('content-type') ?? ''
          if (contentType.includes('text/html')) {
            throw new Error(
              'По ссылке вернулась веб-страница, а не Excel-файл. Откройте таблицу и скопируйте ссылку ' +
                'из адресной строки либо через «Файл → Поделиться → Копировать ссылку», и убедитесь, ' +
                'что доступ открыт всем, у кого есть ссылка.'
            )
          }

          const contentLengthHeader = response.headers.get('content-length')
          const contentLength = contentLengthHeader
            ? Number(contentLengthHeader)
            : null
          if (
            !options?.ignoreSizeLimit &&
            contentLength !== null &&
            Number.isFinite(contentLength) &&
            contentLength > CLIENT_PARSE_SIZE_LIMIT
          ) {
            if (!isCurrentRequest()) {
              toast.dismiss(toastId)
              return
            }
            setOversizedBytes(contentLength)
            toast.dismiss(toastId)
            return
          }

          arrayBuffer = await response.arrayBuffer()
          if (!isCurrentRequest()) {
            toast.dismiss(toastId)
            return
          }

          const signature = new Uint8Array(
            arrayBuffer,
            0,
            Math.min(2, arrayBuffer.byteLength)
          )
          if (signature[0] !== 0x50 || signature[1] !== 0x4b) {
            throw new Error(
              'Файл по ссылке не похож на Excel-файл (.xlsx или .xlsm). Проверьте, что ссылка ведёт на таблицу, ' +
                'а не на папку или документ другого типа.'
            )
          }

          downloadedBufferRef.current = { url: requestUrl, buffer: arrayBuffer }

          if (
            !options?.ignoreSizeLimit &&
            arrayBuffer.byteLength > CLIENT_PARSE_SIZE_LIMIT
          ) {
            if (!isCurrentRequest()) {
              toast.dismiss(toastId)
              return
            }
            setOversizedBytes(arrayBuffer.byteLength)
            toast.dismiss(toastId)
            return
          }
        }

        await applyParsedWorkbook(arrayBuffer, isCurrentRequest, toastId)
      } catch (error) {
        toast.dismiss(toastId)
        if (!isCurrentRequest()) return
        toast.error(
          error instanceof Error
            ? error.message
            : 'Не удалось открыть Excel-файл'
        )
      } finally {
        if (isCurrentRequest()) setLoading(false)
      }
    },
    [applyParsedWorkbook, url]
  )

  const handleLocalFile = useCallback(
    async (file: File, options?: { ignoreSizeLimit?: boolean }) => {
      const requestVersion = ++loadRequestVersionRef.current
      const isCurrentRequest = () =>
        requestVersion === loadRequestVersionRef.current
      localFileRef.current = file
      setLoading(true)
      setOversizedBytes(null)
      const toastId = toast.loading('Читаю Excel-файл...')
      try {
        if (!options?.ignoreSizeLimit && file.size > CLIENT_PARSE_SIZE_LIMIT) {
          setOversizedBytes(file.size)
          toast.dismiss(toastId)
          return
        }

        const arrayBuffer = await file.arrayBuffer()
        if (!isCurrentRequest()) {
          toast.dismiss(toastId)
          return
        }

        const signature = new Uint8Array(
          arrayBuffer,
          0,
          Math.min(4, arrayBuffer.byteLength)
        )
        const isZipBased = signature[0] === 0x50 && signature[1] === 0x4b // .xlsx/.xlsm
        const isLegacyOle =
          signature[0] === 0xd0 &&
          signature[1] === 0xcf &&
          signature[2] === 0x11 &&
          signature[3] === 0xe0 // .xls
        if (!isZipBased && !isLegacyOle) {
          throw new Error(
            'Это не Excel-файл формата .xlsx, .xlsm или .xls. Пересохраните прайс и попробуйте снова.'
          )
        }

        setLocalFileName(file.name)
        await applyParsedWorkbook(arrayBuffer, isCurrentRequest, toastId)
      } catch (error) {
        toast.dismiss(toastId)
        if (!isCurrentRequest()) return
        toast.error(
          error instanceof Error
            ? error.message
            : 'Не удалось открыть Excel-файл'
        )
      } finally {
        if (isCurrentRequest()) setLoading(false)
      }
    },
    [applyParsedWorkbook]
  )

  const handleRetryOversized = useCallback(() => {
    const file = localFileRef.current
    if (showLocalDropzone && file) {
      void handleLocalFile(file, { ignoreSizeLimit: true })
      return
    }

    if (!localFile) void handleLoad({ ignoreSizeLimit: true })
  }, [handleLoad, handleLocalFile, localFile, showLocalDropzone])

  const handleColumnRoleChange = useCallback(
    (columnIndex: number, role?: ColumnRole) => {
      const columnNumber = toColumnNumber(columnIndex)
      const nextValues: ExcelMapperValues = {}

      roles.forEach((item) => {
        if (values[item.value] === columnNumber) {
          nextValues[item.value] = undefined
        }
      })

      if (role) {
        nextValues[role] = columnNumber
      }

      updateMapping(nextValues)
    },
    [roles, values, updateMapping]
  )

  const handleRowSelect = useCallback(
    (rowNumber: number) => {
      if (
        typeof watchedStartRowIndex !== 'number' ||
        typeof watchedEndRowIndex === 'number'
      ) {
        updateMapping({
          startRowIndex: rowNumber,
          endRowIndex: undefined,
        })
        return
      }

      if (rowNumber < watchedStartRowIndex) {
        toast.error('Конечная строка не может быть меньше начальной')
        return
      }

      if (rowNumber >= watchedStartRowIndex) {
        updateMapping({ endRowIndex: rowNumber })
      }
    },
    [updateMapping, watchedEndRowIndex, watchedStartRowIndex]
  )

  const handleStartRowSelect = useCallback(
    (rowNumber: number) => {
      const shouldResetEnd =
        typeof watchedEndRowIndex === 'number' && watchedEndRowIndex < rowNumber
      updateMapping({
        startRowIndex: rowNumber,
        ...(shouldResetEnd && { endRowIndex: undefined }),
      })
    },
    [updateMapping, watchedEndRowIndex]
  )

  const handleReset = () => {
    loadRequestVersionRef.current += 1
    if (cacheKey) clearUrlExcelMapperCache(cacheKey)
    downloadedBufferRef.current = null
    localFileRef.current = null
    setLocalFileName(undefined)
    setOversizedBytes(null)
    setSheets([])
    setActiveSheetName(undefined)
    setSelectedSheetNames([])
    setRowsTruncated(false)
    setColumnsTruncated(false)
  }

  return (
    <>
      <div
        className={classNames(
          cn.urlExcelMapper,
          isOrganisationSelected && cn.urlExcelThemeSelected
        )}
      >
        {!isFileLoaded && (
          <div className={cn.urlExcelMapperHeader}>
            <div>
              <strong>Выбор колонок по файлу</strong>
              <span>
                {localFile
                  ? 'Откройте образец прайса с компьютера и заполните поля кликами по таблице. Файл никуда не отправляется и нужен только на время заполнения формы.'
                  : 'Скачивает публичный Excel и заполняет поля настройки кликами по таблице.'}
              </span>
            </div>
            {!localFile && (
              <AtomButton
                variant="secondary"
                onClick={() => handleLoad()}
                loading={loading}
                disabled={disabled}
              >
                Открыть файл
              </AtomButton>
            )}
          </div>
        )}

        {!isFileLoaded && blockedDownloadUrl !== null && (
          <Alert
            type="info"
            showIcon
            className={cn.urlExcelNotice}
            message="Сайт не разрешает открыть файл прямо в браузере"
            description={
              <>
                Загрузке остатков это не мешает: по расписанию файл скачивает
                сервер, проверить это можно кнопкой «Предпросмотр» на следующем
                шаге. Чтобы выбрать колонки кликами,{' '}
                <a
                  href={blockedDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  скачайте файл
                </a>{' '}
                и перетащите его в поле ниже. Файл никуда не отправляется.
              </>
            }
          />
        )}

        {!isFileLoaded && showLocalDropzone && (
          <Upload.Dragger
            accept=".xlsx,.xlsm,.xls"
            multiple={false}
            showUploadList={false}
            disabled={disabled || loading}
            beforeUpload={(file) => {
              void handleLocalFile(file)
              return false
            }}
            className={cn.excelDropzone}
          >
            <p className={cn.excelDropzoneIcon}>
              <InboxOutlined />
            </p>
            <p className={cn.excelDropzoneText}>
              {localFileName ??
                'Перетащите сюда файл прайса или нажмите, чтобы выбрать'}
            </p>
            <p className={cn.excelDropzoneHint}>
              Excel .xlsx, .xlsm или .xls — чтобы не подбирать номера колонок
              вручную
            </p>
          </Upload.Dragger>
        )}

        {oversizedBytes !== null && (
          <Alert
            type="warning"
            showIcon
            className={cn.urlExcelNotice}
            message={`Файл весит ${formatFileSize(oversizedBytes)} — это слишком много, чтобы открыть его прямо в браузере`}
            description={
              <>
                Укажите номера колонок и диапазон строк вручную, а результат
                проверьте кнопкой «Предпросмотр» на следующем шаге — её
                обрабатывает сервер, ограничения браузера там не действуют.
                <div className={cn.urlExcelNoticeAction}>
                  <AtomButton
                    variant="secondary"
                    onClick={handleRetryOversized}
                    loading={loading}
                  >
                    Всё равно попробовать
                  </AtomButton>
                </div>
              </>
            }
          />
        )}

        {isFileLoaded && activeSheet && (
          <>
            <ExcelSheetControls
              sheetNames={sheetNames}
              activeSheetName={activeSheet.name}
              selectedSheetNames={selectedSheetNames}
              onActiveSheetChange={setActiveSheetName}
              onSelectedSheetsChange={applySelectedSheets}
              onReset={handleReset}
            />

            <UrlExcelTable
              activeSheet={activeSheet}
              roles={roles}
              roleByColumn={roleByColumn}
              startRowIndex={watchedStartRowIndex}
              endRowIndex={watchedEndRowIndex}
              onColumnRoleChange={handleColumnRoleChange}
              onRowSelect={handleRowSelect}
              onStartRowSelect={handleStartRowSelect}
            />

            {(rowsTruncated || columnsTruncated) && (
              <div className={cn.urlExcelTableHint}>
                {rowsTruncated && columnsTruncated && (
                  <>
                    Показаны первые {PREVIEW_ROWS_LIMIT} строк и{' '}
                    {PREVIEW_COLUMNS_LIMIT} колонок файла. Остальное обработает
                    сервер.
                  </>
                )}
                {rowsTruncated && !columnsTruncated && (
                  <>
                    Показаны первые {PREVIEW_ROWS_LIMIT} строк файла. Этого
                    хватает, чтобы выбрать начальную строку и колонки, —
                    остальные строки обработает сервер.
                  </>
                )}
                {!rowsTruncated && columnsTruncated && (
                  <>
                    Показаны первые {PREVIEW_COLUMNS_LIMIT} колонок файла. Если
                    нужная колонка дальше — впишите её номер в поле вручную,
                    таблица его не покажет.
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {renderFields?.(isFileLoaded && !columnsTruncated)}
    </>
  )
}

export default memo(UrlExcelMapper)
